import type {
  McpEvent,
  Message,
  CompactionProgressMessage,
  ToolCallMessage,
  ToolResultMessage,
  AssistantResponseMessage,
  TokenUsageEvent,
  FileMessage,
} from "types";
import {
  isAssistantResponseMessage,
  isCompactionProgressMessage,
  McpProgressEventSchema,
  McpLogEventSchema,
  TokenUsageEventSchema,
  CompactionProgressEventSchema,
  CompactionEventSchema,
} from "types";
import { sseParser } from "../sseParser/sseParser";
import type { SseEvent } from "../sseParser/sseParser";
import { isTextBlock } from "utilities/toolResultContent";

/**
 * Coerce the `content` field of a tool_result SSE event into a string.
 */
function normalizeToolResultContent(content: unknown): string {
  // Is `content` null or undefined?
  if (content === undefined || content === null) {
    return "";
  }
  // Is `content` a string?
  if (typeof content === "string") {
    return content;
  }
  // Is `content` an array?
  if (Array.isArray(content)) {
    let coercedElements: string[] = [];
    for (let element of content) {
      // We have no idea what `element` could be.
      // An `element` might be null
      // (it cannot be `undefined` because JSON Arrays cannot have `undefined` values)
      if (element === null) {
        coercedElements.push("null");
      }
      // An `element` might be a string.
      else if (typeof element === "string") {
        coercedElements.push(element);
      }
      // An `element` might be an object like { "type": "text", "text": "...", "id": "lc_..." }
      else if (isTextBlock(element)) {
        coercedElements.push(element.text);
      }
      // Or an `element` might be something unexpected.
      else {
        coercedElements.push(JSON.stringify(element));
      }
    }
    return coercedElements.join("\n\n");
  }
  // If `content` doesn't meet any of our expectations, fallback to JSON.stringify()
  return JSON.stringify(content);
}

export interface ChatStreamParserState {
  eventsProcessed: number;
  messages: Message[];
  unassignedMcpEvents: McpEvent[];
  unassignedTokenUsage: null | TokenUsageEvent["data"];
  errorMessage: null | string;
}

export function parseChatStream(
  state: ChatStreamParserState,
  sseEvents: SseEvent[]
): void {
  // Helper for handling assistant message content chunks.
  const createOrExtendAssistantMessage = (responseChunk: string) => {
    // If the last message is an Assistant response message, then add to it.
    const lastMessageIndex = state.messages.length - 1;
    const lastMessage = state.messages[lastMessageIndex];
    if (lastMessage && isAssistantResponseMessage(lastMessage)) {
      const newLastMessage = {
        ...lastMessage,
        content: lastMessage.content + responseChunk,
      };
      state.messages[lastMessageIndex] = newLastMessage;
      return;
    }
    // Otherwise, create a new Assistant response message.
    state.messages.push({
      role: "assistant",
      uuid: crypto.randomUUID(),
      content: responseChunk,
      mcpEvents: [...state.unassignedMcpEvents],
    });
  };

  // Helper for handling MCP Events.
  // Adds the MCP event to the last assistant message.
  const saveMcpEvent = (mcpEvent: McpEvent) => {
    const lastMessageIndex = state.messages.findLastIndex((msg) =>
      isAssistantResponseMessage(msg)
    );
    const lastAssistantMessage = state.messages[
      lastMessageIndex
    ] as AssistantResponseMessage;
    // There is no last assistant message, so hold these events in a buffer.
    if (lastAssistantMessage === undefined) {
      state.unassignedMcpEvents.push(mcpEvent);
      return;
    }
    // There is an assistant message.
    let newLastAssistantMessage = { ...lastAssistantMessage };
    // Create an MCP event array if one doesn't exist.
    if (newLastAssistantMessage.mcpEvents === undefined) {
      newLastAssistantMessage.mcpEvents = [];
    }
    // Add the new MCP event.
    newLastAssistantMessage.mcpEvents.push(mcpEvent);
    state.messages[lastMessageIndex] = newLastAssistantMessage;
  };

  // Helper for handling token usage events.
  // Adds token usage to the last assistant message.
  const saveTokenUsage = (usageEvent: TokenUsageEvent) => {
    const lastMessageIndex = state.messages.findLastIndex((msg) =>
      isAssistantResponseMessage(msg)
    );
    const lastAssistantMessage = state.messages[
      lastMessageIndex
    ] as AssistantResponseMessage;
    // There is no last assistant message, so hold token usage in a buffer.
    if (lastAssistantMessage === undefined) {
      state.unassignedTokenUsage = usageEvent.data;
      return;
    }

    // There is an assistant message, so add the token usage.
    let newLastAssistantMessage = {
      ...lastAssistantMessage,
      tokenUsage: usageEvent.data,
    };
    state.messages[lastMessageIndex] = newLastAssistantMessage;
  };

  // Helper for handling general errors from the text stream.
  // Adds error text to the last assistant message.
  const saveErrorMessage = (errorMessage: string) => {
    const lastMessageIndex = state.messages.findLastIndex((msg) =>
      isAssistantResponseMessage(msg)
    );
    const lastAssistantMessage = state.messages[
      lastMessageIndex
    ] as AssistantResponseMessage;
    // There is no last assistant message, so hold token usage in a buffer.
    if (lastAssistantMessage === undefined) {
      state.errorMessage = errorMessage;
      return;
    }

    // There is an assistant message, so add the error message.
    let newLastAssistantMessage = {
      ...lastAssistantMessage,
      errorMessage: errorMessage,
    };
    state.messages[lastMessageIndex] = newLastAssistantMessage;
  };

  for (let update of sseEvents) {
    // Skip '[DONE]' events
    if (update.data === "[DONE]") {
      continue;
    }

    if (update.type === "usage") {
      try {
        const usageEvent = TokenUsageEventSchema.parse({
          type: update.type,
          data: JSON.parse(update.data),
        });
        saveTokenUsage(usageEvent);
      } catch (error) {
        // Failing to parse a few updates is not a critical error.
        console.error(error);
      }
    }

    // Parsing updates from the official OpenAI API has never failed for me,
    // but parsing updates from our own internal Python APIs can fail. The
    // issue seems to be related to how langchain adds whitespace to response
    // messages.
    try {
      // TODO: no type exists for this in the OpenAI API client.
      const parsedUpdate = JSON.parse(update.data);

      // Is the update an MCP event? A progress event? A log event?
      const isMcpEvent = parsedUpdate["type"] === "mcp_event";
      const isMcpProgressEvent =
        isMcpEvent && parsedUpdate["event_type"] === "progress";
      const isMcpLogEvent = isMcpEvent && parsedUpdate["event_type"] === "log";

      // Is the update a tool call request message? A tool call result?
      const isToolCall = parsedUpdate["type"] === "tool_call";
      const isToolCallResult = parsedUpdate["type"] === "tool_result";

      // Is the update a file message?
      const isFileMessage = parsedUpdate["role"] === "file";

      // Is the update a compaction progress event or a final compaction message?
      const isCompactionProgress = update.type === "compaction_progress";
      const isCompaction = update.type === "compaction";

      // Is the update a general error?
      // These events look like:
      //   {"error": {"message": "An error occurred..."}}
      // and happen in various circumstances such as VertexAI/Claude API outages.
      // Note: Tool results can have "error": true (boolean), so we need to check
      // that error is an object before checking for message property.
      const isGeneralError =
        "error" in parsedUpdate &&
        typeof parsedUpdate.error === "object" &&
        parsedUpdate.error !== null &&
        "message" in parsedUpdate.error;

      // Lastly, is the update part of the assistant response? We assume so if
      // the event contains a "choices" property.
      const isAssistantResponse = "choices" in parsedUpdate;

      // MCP progress event
      if (isMcpProgressEvent) {
        const progressUpdate = McpProgressEventSchema.safeParse(parsedUpdate);
        if (progressUpdate.success) {
          saveMcpEvent(progressUpdate.data);
        }
      }
      // MCP log event
      else if (isMcpLogEvent) {
        const logUpdate = McpLogEventSchema.safeParse(parsedUpdate);
        if (logUpdate.success) {
          saveMcpEvent(logUpdate.data);
        }
      }
      // Tool call message
      else if (isToolCall) {
        const toolCallMessage: ToolCallMessage = {
          uuid: crypto.randomUUID(),
          content: parsedUpdate["content"] ?? "",
          role: "assistant",
          id: parsedUpdate["id"],
          tool_name: parsedUpdate["tool_name"],
          tool_input: parsedUpdate["tool_input"],
          mcp_server: parsedUpdate["mcp_server"],
        };
        state.messages.push(toolCallMessage);
      }
      // Tool result message
      else if (isToolCallResult) {
        let toolResultMessage: ToolResultMessage = {
          uuid: crypto.randomUUID(),
          content: normalizeToolResultContent(parsedUpdate["content"]),
          role: "tool",
          tool_call_id: parsedUpdate["tool_call_id"],
          tool_name: parsedUpdate["tool_name"],
        };
        if (parsedUpdate.error !== undefined) {
          toolResultMessage.isError = parsedUpdate.error;
        }
        if (parsedUpdate["task_id"] !== undefined) {
          toolResultMessage.task_id = parsedUpdate["task_id"];
        }
        if (parsedUpdate["task_poll_interval"] !== undefined) {
          toolResultMessage.task_poll_interval =
            parsedUpdate["task_poll_interval"];
        }
        if (parsedUpdate["task_name"] !== undefined) {
          toolResultMessage.task_name = parsedUpdate["task_name"];
        }
        state.messages.push(toolResultMessage);
      }
      // File message
      else if (isFileMessage) {
        const fileMessage: FileMessage = {
          uuid: crypto.randomUUID(),
          content: parsedUpdate["content"] ?? "",
          role: "file",
        };
        if (parsedUpdate["meta"] !== undefined) {
          fileMessage.meta = parsedUpdate["meta"];
        }
        state.messages.push(fileMessage);
      }
      // Compaction progress event
      else if (isCompactionProgress) {
        try {
          const parsed = CompactionProgressEventSchema.parse(parsedUpdate);
          const progressMessage: CompactionProgressMessage = {
            uuid: "compaction-progress",
            content: "",
            role: "compaction_progress",
            ...parsed,
          };
          const existingIndex = state.messages.findIndex(
            isCompactionProgressMessage
          );
          if (existingIndex !== -1) {
            state.messages[existingIndex] = progressMessage;
          } else {
            state.messages.push(progressMessage);
          }
        } catch (error) {
          console.error(error);
        }
      }
      // Compaction message — insert after the boundary message it covers
      else if (isCompaction) {
        try {
          const parsed = CompactionEventSchema.parse(parsedUpdate);
          const progressIndex = state.messages.findIndex(
            isCompactionProgressMessage
          );
          if (progressIndex !== -1) {
            state.messages.splice(progressIndex, 1);
          }
          const boundaryIndex = state.messages.findIndex(
            (msg) => msg.uuid === parsed.covers_through_message_uuid
          );
          if (boundaryIndex !== -1) {
            state.messages.splice(boundaryIndex + 1, 0, parsed);
          }
        } catch (error) {
          console.error(error);
        }
      }
      // General error
      else if (isGeneralError) {
        const backendMessage = parsedUpdate.error.message;
        const errorText =
          typeof backendMessage === "string" && backendMessage.length > 0
            ? `**Error:** ${backendMessage}`
            : "**General server error!** It could be that an LLM provider is unavailable or overloaded. Please try again later.";
        createOrExtendAssistantMessage(errorText);
        saveErrorMessage(errorText);
        console.error(errorText);
      }
      // Assistant response chunk
      else if (isAssistantResponse) {
        // Assume this is text from an assistant response.
        const responseChunk = parsedUpdate.choices[0].delta?.content ?? "";
        createOrExtendAssistantMessage(responseChunk);
      }

      // Mark down the event as successfully processed.
      state.eventsProcessed += 1;
    } catch (error) {
      // Failing to parse a few updates is not a critical error.
      console.error(error);
    }
  }
}

/**
 * Helper function that keeps track of the number of events parsed so far in
 * order to avoid reprocessing events from a single chat_completions request.
 */
export function makeChatStreamParser(initialMessages: Message[]) {
  let state: ChatStreamParserState = {
    eventsProcessed: 0,
    messages: JSON.parse(JSON.stringify(initialMessages)),
    // If a chat completions response starts with tool calls, then there won't
    // be an assistant message to attach MCP events to. Hold them temporarily
    // until an assistant message appears.
    unassignedMcpEvents: [],
    // Same with token usage.
    unassignedTokenUsage: null,
    errorMessage: null,
  };

  return (streamText: string): ChatStreamParserState["messages"] => {
    const sseEvents = sseParser(streamText);
    const newSseEvents = sseEvents.slice(state.eventsProcessed);

    parseChatStream(state, newSseEvents);

    return [...state.messages];
  };
}
